import { useEffect, useState } from 'react';
import { AlertCircle, Info, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api } from '../services/crmApi';
import { useAuth } from '../contexts/AuthContext';

interface BannerData {
  active: boolean;
  message: string;
  type: 'info' | 'warning' | 'error';
  targetRole: 'all' | 'admin';
  updatedAt?: string;
}

// Generates a stable, deterministic key from the banner content
function getBannerKey(banner: BannerData): string {
  // Use a simple hash of the message + updatedAt to create a unique key
  const raw = `${banner.message}_${banner.updatedAt || 'static'}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const char = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32bit integer
  }
  return `crm_banner_dismissed_${Math.abs(hash)}`;
}

export const GlobalBanner: React.FC = () => {
  const { user, signed } = useAuth();
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [dismissed, setDismissed] = useState(true); // Start hidden

  useEffect(() => {
    if (!signed) return;

    const fetchBanner = async () => {
      try {
        const res = await api.get('/api/system/banner');
        if (res.data && res.data.active) {
          setBanner(res.data);
          const key = getBannerKey(res.data);
          const wasDismissed = localStorage.getItem(key) === 'dismissed';
          setDismissed(wasDismissed);
        }
      } catch {
        // Silently ignore banner errors
      }
    };
    
    fetchBanner();
  }, [signed]);

  const handleDismiss = () => {
    setDismissed(true);
    if (banner) {
      const key = getBannerKey(banner);
      localStorage.setItem(key, 'dismissed');
    }
  };

  if (!banner || !banner.active || dismissed || !signed) return null;

  // Role filter
  const isUserAdmin = user?.role?.toLowerCase()?.includes('admin');
  if (banner.targetRole === 'admin' && !isUserAdmin) return null;

  const styles = {
    info:    'bg-blue-600 text-white',
    warning: 'bg-amber-500 text-slate-900',
    error:   'bg-red-600 text-white'
  };

  const Icons = {
    info: Info,
    warning: AlertTriangle,
    error: AlertCircle
  };

  const Icon = Icons[banner.type] || Info;

  return (
    <div className={`w-full ${styles[banner.type]} px-4 py-3 flex items-center justify-between shadow-md z-50 sticky top-0`}>
      <div className="flex items-center gap-3">
        <Icon size={20} className="flex-shrink-0" />
        <p className="text-sm font-medium">{banner.message}</p>
      </div>
      <button 
        onClick={handleDismiss} 
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-bold transition-colors"
      >
        <CheckCircle2 size={14} />
        OK, entendi
      </button>
    </div>
  );
};
